import { useState } from "react";
import { CheckCircle2, X } from "lucide-react";
import { api } from "@/lib/api";
import { autorDe, formatFechaHora, type ObservacionResumen } from "@/components/ComentariosDocumento";

/**
 * Diálogos de acción sobre un documento: comentar, cargar nueva versión y
 * retirarlo del expediente.
 *
 * Viven aparte porque los usan dos sitios: el visor y el listado de documentos
 * de la solicitud. Las acciones tienen que estar donde el usuario mira la lista
 * —no todas las pantallas abren el visor—, y duplicarlos habría dejado dos
 * versiones que se desincronizan.
 */

/** Botón de la barra de acciones del visor. */
export function BotonAccion({
  children, icon, onClick, peligro = false,
}: {
  children: React.ReactNode;
  icon: React.ReactNode;
  onClick: () => void;
  peligro?: boolean;
}) {
  const tono = peligro
    ? "border-estado-err text-estado-err hover:bg-estado-err-bg"
    : "border-primary-strong text-primary-strong hover:bg-primary-tint";
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex cursor-pointer items-center gap-1 rounded border px-2.5 py-1 text-xs font-medium transition ${tono}`}
    >
      {icon}
      {children}
    </button>
  );
}

/** Contenedor común de los diálogos de acción. */
export function DialogoAccion({
  titulo, children, onClose,
}: {
  titulo: string;
  children: React.ReactNode;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-lg bg-surface shadow-overlay">
        <div className="flex items-center justify-between border-b px-4 py-3">
          <h3 className="text-sm font-semibold">{titulo}</h3>
          <button onClick={onClose} className="cursor-pointer rounded p-1 text-fg-subtle transition hover:bg-surface-2">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="space-y-3 p-4">{children}</div>
      </div>
    </div>
  );
}

/**
 * Comentario sobre la versión mostrada. Queda con usuario, fecha y hora.
 *
 * Al guardar, el diálogo NO se cierra solo: muestra el comentario tal como
 * quedó registrado —texto, autor y fecha que devolvió el backend— y espera a
 * que el usuario cierre. Cerrar en silencio dejaba la duda de si se había
 * guardado, y el usuario volvía a capturar el mismo comentario.
 *
 * `onHecho` refresca lo que está detrás; cerrar es cosa de `onClose`.
 */
export function ModalComentario({
  versionId, onClose, onHecho,
}: {
  versionId: string;
  onClose: () => void;
  onHecho: () => Promise<void>;
}) {
  const [texto, setTexto] = useState("");
  const [guardando, setGuardando] = useState(false);
  const [guardado, setGuardado] = useState<ObservacionResumen | null>(null);
  const [error, setError] = useState<string | null>(null);

  const guardar = async () => {
    if (!texto.trim()) { setError("Escribe el comentario."); return; }
    setGuardando(true);
    setError(null);
    try {
      const { data } = await api.post<ObservacionResumen>(
        `/api/documentos/versiones/${versionId}/observaciones`,
        { tipo: "COMENTARIO", texto: texto.trim() },
      );
      setGuardado(data);
      await onHecho();
    } catch (e: unknown) {
      setError(detalleDeError(e, "No se pudo guardar el comentario. Vuelve a intentarlo."));
    } finally {
      setGuardando(false);
    }
  };

  if (guardado) {
    return (
      <DialogoAccion titulo="Comentario guardado" onClose={onClose}>
        <div className="flex items-start gap-2 rounded bg-estado-ok-bg p-2 text-xs text-estado-ok">
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
          <span>El comentario quedó guardado en el expediente del documento.</span>
        </div>
        <blockquote className="whitespace-pre-wrap border-l-2 border-edge-strong pl-2 text-sm text-fg">
          {guardado.texto}
        </blockquote>
        <p className="text-[11px] text-fg-subtle">
          Registrado por {autorDe(guardado)} · {formatFechaHora(guardado.fechaObservacion)}
        </p>
        <div className="flex justify-end pt-1">
          <button onClick={onClose}
                  className="cursor-pointer rounded bg-primary px-3 py-1.5 text-sm font-medium text-white transition hover:bg-primary-hover">
            Cerrar
          </button>
        </div>
      </DialogoAccion>
    );
  }

  return (
    <DialogoAccion titulo="Agregar comentario" onClose={onClose}>
      <textarea
        value={texto}
        onChange={(e) => { setTexto(e.target.value); setError(null); }}
        rows={4}
        autoFocus
        placeholder="Ej. Se solicita sustituir el archivo porque corresponde a otro modelo."
        className="w-full rounded border border-edge bg-surface p-2 text-sm"
      />
      <p className="text-[11px] text-fg-subtle">
        Se guardará con tu usuario y la fecha y hora actuales.
      </p>
      {error && <ErrorLinea>{error}</ErrorLinea>}
      <BotonesDialogo onClose={onClose} onConfirmar={guardar} cargando={guardando} confirmar="Guardar comentario" />
    </DialogoAccion>
  );
}

/** Sustituye el documento con una versión nueva; la anterior queda como histórico. */
export function ModalNuevaVersion({
  documentoId, onClose, onHecho,
}: {
  documentoId: string;
  onClose: () => void;
  onHecho: () => Promise<void>;
}) {
  const [archivo, setArchivo] = useState<File | null>(null);
  const [motivo, setMotivo] = useState("");
  const [subiendo, setSubiendo] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const subir = async () => {
    if (!archivo) { setError("Selecciona el archivo de la nueva versión."); return; }
    setSubiendo(true);
    setError(null);
    try {
      const form = new FormData();
      form.append("archivo", archivo);
      await api.post(`/api/documentos/${documentoId}/versiones`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // El motivo se guarda como comentario de la versión nueva: el endpoint de
      // versiones no lo recibe, y perderlo dejaría el cambio sin explicación.
      if (motivo.trim()) {
        const { data } = await api.get<{ versionVigenteId: string }>(`/api/documentos/${documentoId}`);
        await api.post(`/api/documentos/versiones/${data.versionVigenteId}/observaciones`, {
          tipo: "COMENTARIO",
          texto: motivo.trim(),
        });
      }
      await onHecho();
    } catch (e: unknown) {
      setError(detalleDeError(e, "No se pudo subir la nueva versión."));
      setSubiendo(false);
    }
  };

  return (
    <DialogoAccion titulo="Cargar nueva versión" onClose={onClose}>
      <input
        type="file"
        onChange={(e) => { setArchivo(e.target.files?.[0] ?? null); setError(null); }}
        className="block w-full text-sm file:mr-3 file:cursor-pointer file:rounded file:border-0 file:bg-primary file:px-3 file:py-1.5 file:text-sm file:text-white hover:file:bg-primary-hover"
      />
      <textarea
        value={motivo}
        onChange={(e) => setMotivo(e.target.value)}
        rows={3}
        placeholder="Motivo de la sustitución (opcional, se guarda como comentario)"
        className="w-full rounded border border-edge bg-surface p-2 text-sm"
      />
      <p className="text-[11px] text-fg-subtle">
        La versión actual no se borra: queda en el historial como sustituida.
      </p>
      {error && <ErrorLinea>{error}</ErrorLinea>}
      <BotonesDialogo onClose={onClose} onConfirmar={subir} cargando={subiendo} confirmar="Subir versión" />
    </DialogoAccion>
  );
}

/** Retira el documento del expediente. Nunca borra el archivo. */
export function ModalEliminar({
  documentoId, nombreArchivo, esBorrador, onClose, onHecho,
}: {
  documentoId: string;
  nombreArchivo: string;
  esBorrador: boolean;
  onClose: () => void;
  onHecho: () => Promise<void>;
}) {
  const [motivo, setMotivo] = useState("");
  const [borrando, setBorrando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const eliminar = async () => {
    if (!motivo.trim()) { setError("Escribe el motivo: queda en el expediente."); return; }
    setBorrando(true);
    setError(null);
    try {
      await api.delete(`/api/documentos/${documentoId}`, { data: { motivo: motivo.trim() } });
      await onHecho();
    } catch (e: unknown) {
      setError(detalleDeError(e, "No se pudo retirar el documento."));
      setBorrando(false);
    }
  };

  return (
    <DialogoAccion titulo="¿Deseas eliminar este documento?" onClose={onClose}>
      <p className="text-sm text-fg">
        <span className="font-medium">{nombreArchivo}</span> dejará de formar parte del expediente.
      </p>
      <p className="text-[11px] text-fg-subtle">
        {esBorrador
          ? "El trámite aún no se ha enviado. El archivo se conserva en el histórico junto con el motivo."
          : "El trámite ya fue enviado: el archivo NO se borra, se marca como eliminado y queda en el histórico con el motivo."}
      </p>
      <textarea
        value={motivo}
        onChange={(e) => { setMotivo(e.target.value); setError(null); }}
        rows={3}
        autoFocus
        placeholder="Ej. Archivo cargado por error; corresponde a otro modelo."
        className="w-full rounded border border-edge bg-surface p-2 text-sm"
      />
      {error && <ErrorLinea>{error}</ErrorLinea>}
      <BotonesDialogo onClose={onClose} onConfirmar={eliminar} cargando={borrando}
                      confirmar="Eliminar documento" peligro />
    </DialogoAccion>
  );
}

function BotonesDialogo({
  onClose, onConfirmar, cargando, confirmar, peligro = false,
}: {
  onClose: () => void;
  onConfirmar: () => void;
  cargando: boolean;
  confirmar: string;
  peligro?: boolean;
}) {
  return (
    <div className="flex justify-end gap-2 pt-1">
      <button onClick={onClose} disabled={cargando}
              className="cursor-pointer rounded border px-3 py-1.5 text-sm text-fg-muted transition hover:bg-surface-2 disabled:opacity-50">
        Cancelar
      </button>
      <button onClick={onConfirmar} disabled={cargando}
              className={`cursor-pointer rounded px-3 py-1.5 text-sm font-medium text-white transition disabled:opacity-50 ${
                peligro ? "bg-estado-err hover:opacity-90" : "bg-primary hover:bg-primary-hover"}`}>
        {cargando ? "Guardando…" : confirmar}
      </button>
    </div>
  );
}

function ErrorLinea({ children }: { children: React.ReactNode }) {
  return <div className="rounded bg-estado-err-bg p-2 text-xs text-estado-err">{children}</div>;
}

/** El backend explica en `detail` por qué no se pudo; mostrarlo evita el «error genérico». */
function detalleDeError(e: unknown, porOmision: string): string {
  const r = (e as { response?: { data?: { detail?: string; title?: string } } }).response?.data;
  return r?.detail || r?.title || porOmision;
}
